import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LookupStore } from '@core/lookup/lookup.store';
import { LOOKUP_CODES, RISK_SCREENER_LOOKUP_CODES } from '@core/lookup/lookup-codes';
import { SHARED_IMPORTS } from '../../../../../../shared/modules/shared-imports.module';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { InterventionStore } from '@features/intervention/intervention.store';
import { RiskScreenerRequest, RiskScreenerResponse } from '@features/intervention/intervention.model';
import { SCREENER_EVENTS, SCREENER_PRESENTATIONS, SCREENER_RISK_LEVELS, isAutomaticallyHigh } from '../../risk-screener';
import { toDateOnlyString } from '@shared/utils/date-utils';
import { nullIfBlank } from '@shared/utils/payload-utils';

// The Suicide Risk Screener (IW EAP MW19) for an intervention: filled in on /risk-screeners/new, read back
// (read-only) on /risk-screeners/:screenerId. The questions follow the form; a follow-up appears only when the
// answer before it calls for one ("If No, skip to 10").
@Component({
  selector: 'app-risk-screener-page',
  imports: [RouterLink, ReactiveFormsModule, ...SharedMaterialUIModule, ...SHARED_IMPORTS],
  templateUrl: './risk-screener.page.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RiskScreenerPage {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly lookupStore = inject(LookupStore);
  protected readonly store = inject(InterventionStore);

  protected readonly interventionId = this.route.snapshot.paramMap.get('id') ?? '';
  protected readonly screenerId = this.route.snapshot.paramMap.get('screenerId');
  protected readonly viewing = this.screenerId !== null;
  protected readonly today = new Date();

  // The answer scales are lookups, in the order the form lists them.
  protected readonly frequencies = computed(() => this.lookupStore.items(LOOKUP_CODES.riskScreenerFrequency));
  protected readonly attemptCounts = computed(() => this.lookupStore.items(LOOKUP_CODES.riskScreenerAttemptCount));
  protected readonly lastAttempts = computed(() => this.lookupStore.items(LOOKUP_CODES.riskScreenerLastAttempt));
  protected readonly intensities = computed(() => this.lookupStore.items(LOOKUP_CODES.riskScreenerIntensity));
  protected readonly planLikelihoods = computed(() => this.lookupStore.items(LOOKUP_CODES.riskScreenerPlanLikelihood));
  protected readonly contactFrequencies = computed(() => this.lookupStore.items(LOOKUP_CODES.riskScreenerContactFrequency));
  protected readonly events = SCREENER_EVENTS;
  protected readonly presentations = SCREENER_PRESENTATIONS;
  protected readonly riskLevels = SCREENER_RISK_LEVELS;

  // Questions 1-4, which share one scale.
  protected readonly feelings = [
    { field: 'feltSadId', number: 1, question: 'In the past 4 weeks did you feel so sad that nothing could cheer you up?' },
    { field: 'feltNoHopeId', number: 2, question: 'In the past 4 weeks, how often did you feel no hope for the future?' },
    { field: 'feltShameOrGuiltId', number: 3, question: 'In the past 4 weeks, how often did you feel intense shame or guilt?' },
    { field: 'feltWorthlessId', number: 4, question: 'In the past 4 weeks, how often did you feel worthless?' },
  ] as const;

  protected readonly form = this.formBuilder.nonNullable.group({
    screenedDate: this.formBuilder.control<Date | null>(new Date()),
    feltSadId: this.formBuilder.control<string | null>(null),
    feltNoHopeId: this.formBuilder.control<string | null>(null),
    feltShameOrGuiltId: this.formBuilder.control<string | null>(null),
    feltWorthlessId: this.formBuilder.control<string | null>(null),
    attemptedSuicide: this.formBuilder.control<boolean | null>(null),
    attemptCountId: this.formBuilder.control<string | null>(null),
    lastAttemptId: this.formBuilder.control<string | null>(null),
    changesSinceAttempt: [''],
    recentUpsettingEvents: this.formBuilder.control<boolean | null>(null),
    eventFamilyBreakdown: [false],
    eventSexualIdentityConflict: [false],
    eventChildCustody: [false],
    eventRelationshipProblem: [false],
    eventLossOfLovedOne: [false],
    eventChronicPainOrIllness: [false],
    eventTrauma: [false],
    eventImpendingLegalProsecution: [false],
    eventOther: [''],
    thoughtsOfSuicide: this.formBuilder.control<boolean | null>(null),
    thoughtFrequency: [''],
    thoughtDuration: [''],
    worstThoughtIntensityId: this.formBuilder.control<string | null>(null),
    recentThoughtIntensityId: this.formBuilder.control<string | null>(null),
    hasPlan: this.formBuilder.control<boolean | null>(null),
    planMethod: [''],
    accessToMeans: this.formBuilder.control<boolean | null>(null),
    planLocation: [''],
    preparationsMade: this.formBuilder.control<boolean | null>(null),
    planLikelihoodId: this.formBuilder.control<string | null>(null),
    protectiveFactors: [''],
    hasSupport: this.formBuilder.control<boolean | null>(null),
    supportPeople: [''],
    supportContactFrequencyId: this.formBuilder.control<string | null>(null),
    pastCoping: [''],
    presentsAgitated: [false],
    presentsConfused: [false],
    presentsDelusional: [false],
    presentsOther: [''],
    riskLevelCode: this.formBuilder.control<string | null>(null),
  });

  protected readonly screener = computed<RiskScreenerResponse | null>(
    () => this.store.riskScreeners().find((item) => item.id === this.screenerId) ?? null,
  );

  constructor() {
    this.lookupStore.load(RISK_SCREENER_LOOKUP_CODES);
    this.store.loadOne(this.interventionId);
    this.store.loadRiskScreeners(this.interventionId);

    // The form's own rule: thoughts of suicide while agitated, confused or delusional is High, whatever else is chosen.
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      if (!this.viewing && this.autoHigh() && this.form.controls.riskLevelCode.value !== 'HIGH_RISK') {
        this.form.controls.riskLevelCode.setValue('HIGH_RISK');
      }
    });

    effect(() => {
      const screener = this.screener();
      if (this.viewing && screener) {
        this.populate(screener);
      }
    });

    if (this.viewing) {
      this.form.disable({ emitEvent: false });
    }
  }

  protected autoHigh(): boolean {
    const value = this.form.getRawValue();
    return isAutomaticallyHigh(value.thoughtsOfSuicide, [value.presentsAgitated, value.presentsConfused, value.presentsDelusional]);
  }

  protected selectedLevel() {
    return this.riskLevels.find((level) => level.code === this.form.controls.riskLevelCode.value) ?? null;
  }

  // Mirrors the server: every question the form insists on is answered, and so is each follow-up that applies.
  protected canSave(): boolean {
    const v = this.form.getRawValue();
    const answered = (value: string | null) => !!value?.trim();

    const feelings = !!v.feltSadId && !!v.feltNoHopeId && !!v.feltShameOrGuiltId && !!v.feltWorthlessId;
    const attempt = v.attemptedSuicide !== null && (!v.attemptedSuicide || (!!v.attemptCountId && !!v.lastAttemptId));
    const anyEvent =
      this.events.some((item) => v[item.field]) || answered(v.eventOther);
    const upsetting = v.recentUpsettingEvents !== null && (!v.recentUpsettingEvents || anyEvent);
    const plan =
      v.hasPlan !== null &&
      (!v.hasPlan ||
        (answered(v.planMethod) && v.accessToMeans !== null && v.preparationsMade !== null && !!v.planLikelihoodId));
    const thoughts =
      v.thoughtsOfSuicide !== null &&
      (!v.thoughtsOfSuicide ||
        (answered(v.thoughtFrequency) &&
          answered(v.thoughtDuration) &&
          !!v.worstThoughtIntensityId &&
          !!v.recentThoughtIntensityId &&
          plan));
    const support =
      v.hasSupport !== null && (!v.hasSupport || (answered(v.supportPeople) && !!v.supportContactFrequencyId));

    return feelings && attempt && upsetting && thoughts && support && !!v.riskLevelCode && !this.store.savingRiskScreener();
  }

  protected save(): void {
    if (this.viewing || !this.canSave()) {
      return;
    }

    const v = this.form.getRawValue();
    const riskRatingId =
      this.lookupStore.items(LOOKUP_CODES.riskRating).find((item) => item.code === v.riskLevelCode)?.id ?? null;
    const text = nullIfBlank;

    const request: RiskScreenerRequest = {
      screenedDate: v.screenedDate ? toDateOnlyString(v.screenedDate) : null,
      feltSadId: v.feltSadId,
      feltNoHopeId: v.feltNoHopeId,
      feltShameOrGuiltId: v.feltShameOrGuiltId,
      feltWorthlessId: v.feltWorthlessId,
      attemptedSuicide: v.attemptedSuicide,
      attemptCountId: v.attemptCountId,
      lastAttemptId: v.lastAttemptId,
      changesSinceAttempt: text(v.changesSinceAttempt),
      recentUpsettingEvents: v.recentUpsettingEvents,
      eventFamilyBreakdown: v.eventFamilyBreakdown,
      eventSexualIdentityConflict: v.eventSexualIdentityConflict,
      eventChildCustody: v.eventChildCustody,
      eventRelationshipProblem: v.eventRelationshipProblem,
      eventLossOfLovedOne: v.eventLossOfLovedOne,
      eventChronicPainOrIllness: v.eventChronicPainOrIllness,
      eventTrauma: v.eventTrauma,
      eventImpendingLegalProsecution: v.eventImpendingLegalProsecution,
      eventOther: text(v.eventOther),
      thoughtsOfSuicide: v.thoughtsOfSuicide,
      thoughtFrequency: text(v.thoughtFrequency),
      thoughtDuration: text(v.thoughtDuration),
      worstThoughtIntensityId: v.worstThoughtIntensityId,
      recentThoughtIntensityId: v.recentThoughtIntensityId,
      hasPlan: v.hasPlan,
      planMethod: text(v.planMethod),
      accessToMeans: v.accessToMeans,
      planLocation: text(v.planLocation),
      preparationsMade: v.preparationsMade,
      planLikelihoodId: v.planLikelihoodId,
      protectiveFactors: text(v.protectiveFactors),
      hasSupport: v.hasSupport,
      supportPeople: text(v.supportPeople),
      supportContactFrequencyId: v.supportContactFrequencyId,
      pastCoping: text(v.pastCoping),
      presentsAgitated: v.presentsAgitated,
      presentsConfused: v.presentsConfused,
      presentsDelusional: v.presentsDelusional,
      presentsOther: text(v.presentsOther),
      riskRatingId,
    };

    this.store.createRiskScreener(this.interventionId, request, () =>
      void this.router.navigate(['/interventions', this.interventionId]),
    );
  }

  private populate(screener: RiskScreenerResponse): void {
    this.form.patchValue(
      {
        ...screener,
        screenedDate: new Date(`${screener.screenedDate}T00:00:00`),
        changesSinceAttempt: screener.changesSinceAttempt ?? '',
        eventOther: screener.eventOther ?? '',
        thoughtFrequency: screener.thoughtFrequency ?? '',
        thoughtDuration: screener.thoughtDuration ?? '',
        planMethod: screener.planMethod ?? '',
        planLocation: screener.planLocation ?? '',
        protectiveFactors: screener.protectiveFactors ?? '',
        supportPeople: screener.supportPeople ?? '',
        pastCoping: screener.pastCoping ?? '',
        presentsOther: screener.presentsOther ?? '',
        riskLevelCode: screener.riskRatingCode,
      },
      { emitEvent: false },
    );
  }
}
